import React, { useState, useEffect, useMemo } from "react";
import { Navigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import JBrowseService from "../services/jbrowse.service";

// Material UI imports
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Folder as FolderIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Profile = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  console.log("currentUser: ", currentUser);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [companyFiles, setCompanyFiles] = useState([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Group files by type
  const groupedFiles = useMemo(() => {
    const groups = {};
    
    companyFiles.forEach(file => {
      // Ignore files without names or system files
      if (!file.name || file.name.startsWith('.')) return;
      
      // Extract extension
      const extension = file.name.includes('.') 
        ? file.name.split('.').pop().toLowerCase() 
        : 'other';
      
      if (!groups[extension]) {
        groups[extension] = [];
      }
      
      groups[extension].push(file);
    });
    
    // Sort files within each group by name
    Object.keys(groups).forEach(ext => {
      groups[ext].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return groups;
  }, [companyFiles]);

  // Count files by type for summary display
  const fileCounts = useMemo(() => {
    return Object.entries(groupedFiles).map(([extension, files]) => ({
      extension,
      count: files.length,
      totalSize: files.reduce((sum, file) => sum + file.size, 0)
    })).sort((a, b) => b.count - a.count);
  }, [groupedFiles]);

  useEffect(() => {
    if (currentUser?.DirectoryName) {
      setFileLoading(true);
      setFileError(null);
      console.log("Making request with directory:", currentUser.DirectoryName);
      JBrowseService.getCompanyFiles(currentUser.DirectoryName)
        .then(response => {
          console.log("company files response:", response);
          setCompanyFiles(response.files || []);
        })
        .catch(error => {
          console.error("Error fetching files:", error.response || error);
          setFileError(error.response?.data?.error || 'Failed to fetch company files');
          setNotification({ severity: 'error', message: 'Failed to fetch company files' });
        })
        .finally(() => {
          setFileLoading(false);
        });
    }
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedGroup(isExpanded ? panel : null);
  };

  const handlePasswordChange = () => {
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setNotification({ severity: 'error', message: 'All password fields are required.' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotification({ severity: 'error', message: 'New passwords do not match.' });
      return;
    }

    if (newPassword.length < 8) {
      setNotification({ severity: 'error', message: 'New password must be at least 8 characters long.' });
      return;
    }

    setLoading(true);
    JBrowseService.changePassword({
      user: currentUser,
      oldPassword: currentPassword,
      newPassword: newPassword
    }).then(
      (response) => {
        setNotification({ severity: 'success', message: 'Password changed successfully' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      },
      (error) => {
        setNotification({ severity: 'error', message: 'Failed to change password. Please try again.' });
      }
    ).finally(() => {
      setLoading(false);
      setTimeout(() => setNotification(null), 3000);
    });
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get file type label based on extension
  const getFileTypeLabel = (extension) => {
    const typeMap = {
      'fa': 'FASTA',
      'fasta': 'FASTA',
      'gff': 'GFF',
      'gff3': 'GFF3',
      'vcf': 'VCF',
      'bw': 'BigWig',
      'bigwig': 'BigWig'
    };
    
    return typeMap[extension] || extension.toUpperCase();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Profile Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={currentUser.gravatar}
            sx={{ width: 100, height: 100, mr: 2 }}
          />
          <Box>
            <Typography variant="h4" component="h1">
              {currentUser.username}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {currentUser.email}
            </Typography>
          </Box>
        </Box>

        {/* Tabs Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
            <Tab icon={<PersonIcon />} label="Personal Info" />
            <Tab icon={<FolderIcon />} label="File Access" />
            <Tab icon={<SecurityIcon />} label="Security" />
          </Tabs>
        </Box>

        {/* Personal Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <List>
               
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Email"
                    secondary={currentUser.email}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Company"
                    secondary={currentUser.company}
                  />
                </ListItem>
              
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Roles"
                    secondary={currentUser.roles.join(', ')}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </TabPanel>

        {/* File Access Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>File Access</Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Directory: {currentUser.DirectoryName}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                Showing only genomic data files: FASTA, GFF, GFF3, VCF, and BigWig
              </Typography>
              
              {fileLoading ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : fileError ? (
                <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>
              ) : companyFiles.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No supported files found in company directory
                </Typography>
              ) : (
                <>
                  {/* File type summary */}
                  <Box sx={{ mb: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>File Type Summary</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fileCounts.map(({ extension, count, totalSize }) => (
                        <Chip 
                          key={extension}
                          label={`${getFileTypeLabel(extension)}: ${count} (${formatFileSize(totalSize)})`}
                          color="primary"
                          variant="outlined"
                          onClick={() => setExpandedGroup(extension)}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {/* File groups accordions */}
                  {Object.entries(groupedFiles).map(([extension, files]) => (
                    <Accordion 
                      key={extension}
                      expanded={expandedGroup === extension}
                      onChange={handleAccordionChange(extension)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-${extension}-content`}
                        id={`panel-${extension}-header`}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <FileIcon sx={{ mr: 1 }} />
                          <Typography variant="subtitle1">{getFileTypeLabel(extension)} Files</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                            {files.length} file{files.length !== 1 ? 's' : ''} ({formatFileSize(files.reduce((sum, file) => sum + file.size, 0))})
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>File Name</TableCell>
                                <TableCell>Path</TableCell>
                                <TableCell align="right">Size</TableCell>
                                <TableCell align="right">Last Modified</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {files.map((file, index) => (
                                <TableRow key={index}>
                                  <TableCell>{file.name}</TableCell>
                                  <TableCell>{file.path}</TableCell>
                                  <TableCell align="right">{formatFileSize(file.size)}</TableCell>
                                  <TableCell align="right">{formatDate(file.modifiedDate)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Change Password</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Current Password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePasswordChange}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Update Password'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Notification */}
        {notification && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={notification.severity}>
              {notification.message}
            </Alert>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Profile;